// Laying, re-aiming, switching off and lifting track — the half of a click
// that is about the network rather than about a piece.
//
// It owns the drawn run (`core.path`) and nothing else. A drag that reaches a
// second square stands down the click flow, and lifting a part closes the
// picker that was open on it, so both reach fields another verb owns.

import { DIR_OFFSET } from "src/engine/rules/board";
import { isGameOver } from "src/engine/rules/chess/status";
import { canExtendBelt } from "src/engine/rules/industry/belts";
import {
  beltDirAt,
  beltOwnerAt,
  blueprintOwnerAt,
  NO_BELT
} from "src/engine/rules/industry/state";

import type { SessionCore } from "./core";
import { REFUSAL } from "./refusals";
import { actRefusal } from "./turn";

export function beltVerbs(core: SessionCore) {
  /**
   * A path of one is a pointer resting on a square, which is a click until it
   * moves. Previewing it would flash a highlight under every click.
   */
  const drawing = (): boolean => core.path.length > 1;

  /**
   * A **built** part of ours on this square.
   *
   * Deliberately not a drawn one. A blueprint is a claim about where track is
   * going rather than a thing on the board: it is not a pick target, so a
   * click in the pieces view never lands on one, and the picker it would open
   * is a control for a part that does not exist yet. What edits a drawn run is
   * the tool that drew it — draw over it to re-aim, or lift it with Erase.
   */
  const myBelt = (square: number): boolean =>
    beltDirAt(core.state.industry, square) !== NO_BELT &&
    beltOwnerAt(core.state.industry, square) === core.localSide;

  /**
   * Arm the belt tool if this square is ground we could build on.
   *
   * Runs on pointer *down*, which is why it changes nothing else: at that
   * moment the gesture is still indistinguishable from a click, and clearing
   * the picker or a half-drawn run here would break every two-click flow —
   * the second click would arrive to find its own first click undone.
   */
  const startPath = (square: number): boolean => {
    const state = core.state;
    if (isGameOver(state.status)) return false;
    // **No ghost off-turn** (§29 U1). Drawing a run the reducer will refuse
    // is a preview of something that cannot happen, and the drag ends in a
    // silent nothing — so the gesture is refused where it starts, out loud.
    const blocked = actRefusal(state, core.localSide, core.viewing);
    if (blocked) {
      core.setRefusal(REFUSAL[blocked]);
      return false;
    }
    // `canExtendBelt`, so a run can be started on a part of your own — which
    // is the gesture that re-aims a whole lane, drawing it again the way you
    // meant it — and on a square one of your own pieces is standing on.
    if (canExtendBelt(state.position, state.industry, square, core.localSide) !== null) {
      return false;
    }
    core.path = [square];
    core.changed();
    return true;
  };

  /** Extend to an orthogonal neighbour. Backtracking one step undoes it. */
  const extendPath = (square: number): void => {
    const current = core.path;
    if (current.length === 0) return;
    const head = current[current.length - 1];
    if (square === head) return;

    let next = current;
    if (current.length > 1 && square === current[current.length - 2]) {
      // Dragging back over the previous square rubs that part out, so a
      // mis-drawn corner is fixable without letting go.
      next = current.slice(0, -1);
    } else if (DIR_OFFSET.includes(square - head) && !current.includes(square)) {
      next = [...current, square];
    }
    if (next === current) return;

    // The pointer has reached a second square: this is a drag, not a click,
    // and only now does it take over from whatever the click flow had going.
    if (current.length === 1) {
      core.pending = undefined;
      core.lineAnchor = undefined;
    }
    core.path = next;
    core.changed();
  };

  const commitPath = (): void => {
    const drawn = core.path;
    core.path = [];
    if (drawn.length > 1) {
      // The clock again, because it can turn while the pointer is down — the
      // opponent moves mid-drag in a run. `startPath` cleared it, but the
      // dispatch is what the reducer sees, and an unchecked one comes back
      // `false` and takes the whole run away without saying anything.
      const blocked = actRefusal(core.state, core.localSide, core.viewing);
      if (blocked) {
        core.setRefusal(REFUSAL[blocked]);
      } else {
        core.send({ type: "lay-belt-path", squares: drawn, owner: core.localSide });
      }
    }
    core.changed();
  };

  /**
   * Switch the open belt off or on. Free of tempo, like rotation.
   *
   * The picker's square when none is named — the hook only ever read the
   * open picker; a square given outright toggles that part instead.
   */
  const toggleBelt = (square?: number): void => {
    const at = square ?? (core.pending?.kind === "rotate" ? core.pending.square : undefined);
    if (at === undefined) return;
    const blocked = actRefusal(core.state, core.localSide, core.viewing);
    if (blocked) {
      core.setRefusal(REFUSAL[blocked]);
      return;
    }
    core.send({ type: "toggle-belt", square: at, owner: core.localSide });
    if (core.pending?.kind === "rotate") core.pending = undefined;
    core.changed();
  };

  /**
   * Take a part off the board. Free of tempo, like laying one.
   *
   * There was no way to do this at all: a part could be laid, re-pointed and
   * switched off, but the only thing that ever *removed* one was an enemy
   * piece standing on it. A lane drawn in the wrong place was permanent.
   *
   * The clock first, and it is the shared door the erase sweep comes through
   * too — so lifting track off-turn says why once per part rather than doing
   * nothing at all.
   */
  const removeBelt = (square: number): void => {
    const state = core.state;
    const blocked = actRefusal(state, core.localSide, core.viewing);
    if (blocked) {
      core.setRefusal(REFUSAL[blocked]);
      return;
    }
    if (
      beltOwnerAt(state.industry, square) !== core.localSide &&
      blueprintOwnerAt(state.industry, square) !== core.localSide
    ) {
      core.setRefusal("That part is not yours to remove.");
      return;
    }
    core.pending = undefined;
    core.cycle = undefined;
    core.send({ type: "remove-belt", square, owner: core.localSide });
    core.changed();
  };

  /**
   * Sweep a square while the remove tool is down.
   *
   * Quiet where `removeBelt` speaks: a sweep crosses bare ground constantly
   * and every one of those squares would otherwise raise "that part is not
   * yours to remove", which is both wrong and unreadable at drag speed. An
   * enemy part still refuses, because that one is worth saying.
   */
  const eraseBelt = (square: number): void => {
    const industry = core.state.industry;
    if (beltOwnerAt(industry, square) < 0 && blueprintOwnerAt(industry, square) < 0) {
      return;
    }
    removeBelt(square);
  };

  /** The picker's centre target was clicked: remove whatever it is open on. */
  const removePicked = (): void => {
    if (core.pending?.kind !== "rotate") return;
    removeBelt(core.pending.square);
  };

  return {
    drawing,
    myBelt,
    startPath,
    extendPath,
    commitPath,
    toggleBelt,
    removeBelt,
    eraseBelt,
    removePicked
  };
}

export type BeltVerbs = ReturnType<typeof beltVerbs>;
